/**
 * Shared predicate for "the auth session has SETTLED into a logged-out state".
 *
 * Every auth provider (Kinde, Auth0, Keycloak) reports `isAuthenticated`
 * as `false` during its initial `loading` phase, before the session has
 * resolved. Treating "not authenticated" as "logged out" without also
 * checking "not loading" is the recurring bug behind both the on-prem
 * stale-cookie redirect (which bounced authenticated users to Keycloak on
 * every fresh load) and cloud's leftover-token zombie clear: the guard has
 * to wait for the session to settle first.
 *
 * This is intentionally dependency-free so both the cloud `<Auth>` wrapper
 * and the on-prem `KeycloakReduxSync` bridge can share one definition of
 * "settled + logged out" rather than each re-deriving `!isLoading &&
 * !isAuthenticated` and risking dropping the loading guard again.
 *
 * Params accept `null` / `undefined` because SDK-backed flags are nullish
 * before init (e.g. Kinde types `isAuthenticated` as `boolean | null`).
 * Nullish is treated as falsy, matching the original `!x` idiom exactly.
 */
export function isSettledLoggedOut(
  isAuthenticated: boolean | null | undefined,
  isLoading: boolean | null | undefined
): boolean {
  return !isLoading && !isAuthenticated;
}
